"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowDownTrayIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { Tooltip } from "react-tooltip";
import useDownloader from "react-use-downloader";
interface Props {
  href: string;
  name: string;
  extension: string;
  showProgress?: boolean;
  className?: string;
  tooltipId?: string;
}
export default function WaveDropActionsDownload(props) {
  const $ = _c(37);
  const {
    percentage,
    download,
    cancel,
    isInProgress
  } = useDownloader();
  const showProgress = props.showProgress ?? true;
  const [isCompleted, setIsCompleted] = useState(false);
  const completionTimeoutRef = useRef(null);
  let t0;
  if ($[0] !== download || $[1] !== props.extension || $[2] !== props.href || $[3] !== props.name) {
    t0 = async function startDownload() {
      const filename = props.extension ? `${props.name}.${props.extension}` : props.name;
      download(props.href, filename);
    };
    $[0] = download;
    $[1] = props.extension;
    $[2] = props.href;
    $[3] = props.name;
    $[4] = t0;
  } else {
    t0 = $[4];
  }
  const startDownload = t0;
  let t1;
  let t2;
  if ($[5] !== percentage || $[6] !== showProgress) {
    t1 = () => {
      if (percentage === 100 && showProgress) {
        setIsCompleted(true);
        if (completionTimeoutRef.current) {
          clearTimeout(completionTimeoutRef.current);
        }
        completionTimeoutRef.current = setTimeout(() => {
          setIsCompleted(false);
          completionTimeoutRef.current = null;
        }, 1500);
      }
    };
    t2 = [percentage, showProgress];
    $[5] = percentage;
    $[6] = showProgress;
    $[7] = t1;
    $[8] = t2;
  } else {
    t1 = $[7];
    t2 = $[8];
  }
  useEffect(t1, t2);
  let t3;
  let t4;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = () => () => {
      if (completionTimeoutRef.current) {
        clearTimeout(completionTimeoutRef.current);
      }
    };
    t4 = [];
    $[9] = t3;
    $[10] = t4;
  } else {
    t3 = $[9];
    t4 = $[10];
  }
  useEffect(t3, t4);
  let t5;
  if ($[11] !== isCompleted || $[12] !== isInProgress || $[13] !== percentage || $[14] !== showProgress) {
    t5 = () => {
      if (isCompleted) {
        return "Downloaded!";
      }
      if (isInProgress && showProgress) {
        return `Downloading ${percentage}%`;
      }
      return "Download media";
    };
    $[11] = isCompleted;
    $[12] = isInProgress;
    $[13] = percentage;
    $[14] = showProgress;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  const getTooltipText = t5;
  let t6;
  if ($[16] !== isCompleted || $[17] !== isInProgress || $[18] !== showProgress) {
    t6 = () => {
      if (isCompleted) {
        return <CheckCircleIcon className="tw-flex-shrink-0 tw-w-5 tw-h-5 tw-text-emerald-500" />;
      }
      if (isInProgress && showProgress) {
        return <svg className="tw-w-5 tw-h-5 tw-text-iron-500 tw-animate-spin tw-flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>;
      }
      return <ArrowDownTrayIcon className="tw-flex-shrink-0 tw-w-5 tw-h-5" />;
    };
    $[16] = isCompleted;
    $[17] = isInProgress;
    $[18] = showProgress;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const renderIcon = t6;
  let t7;
  if ($[20] !== cancel || $[21] !== isInProgress || $[22] !== startDownload) {
    t7 = e => {
      e.stopPropagation();
      if (!isInProgress) {
        startDownload();
      } else {
        cancel();
      }
    };
    $[20] = cancel;
    $[21] = isInProgress;
    $[22] = startDownload;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const t8 = props.tooltipId ?? undefined;
  let t9;
  if ($[24] !== renderIcon) {
    t9 = renderIcon();
    $[24] = renderIcon;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== isCompleted || $[27] !== t7 || $[28] !== t8 || $[29] !== t9) {
    t10 = <button className="tw-text-iron-400 desktop-hover:hover:tw-text-iron-50 tw-px-2 tw-h-full tw-group tw-bg-transparent tw-rounded-full tw-border-0 tw-flex tw-items-center tw-gap-x-2 tw-text-[0.8125rem] tw-leading-5 tw-font-medium tw-transition tw-ease-out tw-duration-300 tw-cursor-pointer" onClick={t7} disabled={isCompleted} aria-label="Download file" data-tooltip-id={t8} type="button">{t9}</button>;
    $[26] = isCompleted;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== getTooltipText || $[32] !== props.tooltipId) {
    t11 = props.tooltipId && <Tooltip id={props.tooltipId} place="top" offset={8} opacity={1} style={{
      padding: "4px 8px",
      background: "#37373E",
      color: "white",
      fontSize: "13px",
      fontWeight: 500,
      borderRadius: "6px",
      boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
      zIndex: 99999,
      pointerEvents: "none"
    }}><span className="tw-text-xs">{getTooltipText()}</span></Tooltip>;
    $[31] = getTooltipText;
    $[32] = props.tooltipId;
    $[33] = t11;
  } else {
    t11 = $[33];
  }
  let t12;
  if ($[34] !== t10 || $[35] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[34] = t10;
    $[35] = t11;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  return t12;
}